import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  ChangeDetectorRef,
  ViewChild,
  AfterViewInit,
  effect,
  ElementRef,
  DestroyRef,
  inject,
  HostListener,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, debounceTime, switchMap, filter, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService } from 'primeng/api';
import Quill from 'quill';
import ImageResize from 'quill-resize-image';

// Register Quill image resize module
Quill.register('modules/ImageResize', ImageResize);

// API imports
import { Api } from '../../../api/api';
import { BlockchainsLookupService } from '../../../services/blockchains-lookup.service';
import {
  adminProjectsControllerGetProjectV1,
  adminProjectsControllerCreateProjectV1,
  adminProjectsControllerUpdateProjectV1,
  adminProjectsControllerAddOrUpdateTeamMemberV1,
  adminProjectsControllerDeleteTeamMemberV1,
  adminProjectsControllerAddOrUpdateVestingCategoryV1,
  adminProjectsControllerDeleteVestingCategoryV1,
  adminProjectsControllerAddOrUpdateUpdateV1,
  adminProjectsControllerDeleteUpdateV1,
  adminProjectsControllerAddOrUpdateTokenAllocationV1,
  adminProjectsControllerDeleteTokenAllocationV1,
  adminProjectsControllerPublishProjectV1,
  adminProjectsControllerUnpublishProjectV1,
  adminProjectsControllerFeatureProjectV1,
  adminProjectsControllerUnfeatureProjectV1,
  adminProjectsControllerDeleteProjectV1,
  adminProjectsControllerApproveProjectV1,
  adminProjectsControllerRejectProjectV1,
  adminCategoriesControllerListCategoriesV1,
  adminProjectOwnersControllerGetAllProjectOwners,
} from '../../../api/functions';
import { ProjectResponseAdminDto } from '../../../api/models/project-response-admin-dto';
import { CreateVestingCategoryDto } from '../../../api/models/create-vesting-category-dto';
import { CreateTokenAllocationDto } from '../../../api/models/create-token-allocation-dto';
import { CategoryResponseAdminDto } from '../../../api/models/category-response-admin-dto';
import { ProjectOwnerResponseAdminAdminDto } from '../../../api/models/project-owner-response-admin-admin-dto';

// Services
import { HeaderService } from '../../../services/header.service';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { EditorModule } from 'primeng/editor';
import { ToastModule } from 'primeng/toast';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { StepperModule } from 'primeng/stepper';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ChartModule } from 'primeng/chart';
import { ChipModule } from 'primeng/chip';
import { UIChart } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TooltipModule } from 'primeng/tooltip';

import {
  TeamMemberDialogComponent,
  TeamMember,
} from '../../../components/team-member-dialog/team-member-dialog.component';
import { TeamMemberItemComponent } from '../../../components/team-member-item/team-member-item.component';
import {
  VestingCategoryDialogComponent,
  VestingCategory,
} from '../../../components/vesting-category-dialog/vesting-category-dialog.component';
import {
  UpdateDialogComponent,
  Update,
} from '../../../components/update-dialog/update-dialog.component';
import { UpdateItemComponent } from '../../../components/update-item/update-item.component';
import {
  OverviewNavItemComponent,
  OverviewNavItem,
} from '../../../components/overview-nav-item/overview-nav-item';
import {
  AllocationDialogComponent,
  TokenAllocation,
} from '../../../components/allocation-dialog/allocation-dialog.component';
import { AdminImageSrcPipe } from '../../../shared/pipes/admin-image-src.pipe';
import { ShortenAddressPipe } from '../../../shared/pipes/shorten-address.pipe';

@Component({
  selector: 'app-project-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    MessageModule,
    SkeletonModule,
    DynamicDialogModule,
    DatePickerModule,
    TableModule,
    ToastModule,
    FileUploadModule,
    EditorModule,
    InputGroupModule,
    InputGroupAddonModule,
    StepperModule,
    FloatLabelModule,
    ChartModule,
    ChipModule,
    CheckboxModule,
    SelectModule,
    MultiSelectModule,
    ConfirmDialogModule,
    AccordionModule,
    AutoCompleteModule,
    TooltipModule,
    TeamMemberItemComponent,
    UpdateItemComponent,
    OverviewNavItemComponent,
    AdminImageSrcPipe,
    ShortenAddressPipe,
  ],
  providers: [DialogService, MessageService, ConfirmationService],
  templateUrl: './project-edit.component.html',
  styleUrl: './project-edit.component.css',
})
export class ProjectEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tokenChart') tokenChart!: UIChart;
  @ViewChild('chartContainer', { read: ElementRef }) chartContainer!: ElementRef;

  // Inject dependencies using modern inject() function
  private readonly api = inject(Api);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly headerService = inject(HeaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly blockchainsLookup = inject(BlockchainsLookupService);

  // Loading states
  protected readonly loading = signal(true);
  protected readonly categoriesLoading = signal(true);
  protected readonly submitting = signal(false);
  protected readonly formValid = signal(false);
  protected readonly autoSaving = signal(false);
  protected readonly projectStatus = signal<string | undefined>(undefined);
  protected readonly projectFeatured = signal(false);

  // Categories data
  protected categories: CategoryResponseAdminDto[] = [];

  // Auto-save functionality
  private autoSaveSubject = new Subject<{ field: string; value: unknown }>();
  private pendingChanges = new Map<string, unknown>(); // Accumulate changes during debounce period
  private isAutoSaveEnabled = false;
  private lastAutoSaveTime = 0;
  private readonly AUTO_SAVE_DEBOUNCE_MS = 2000; // 2 seconds grace period
  private readonly AUTO_SAVE_MIN_INTERVAL_MS = 1000; // Minimum 1 second between saves

  // Chart state signals
  chartInitialized = signal(false);

  // Route and mode state
  protected projectId: string | null = null;
  protected isEditMode = false;

  // Accordion state - tracks which panel is currently open
  accordionValue = signal<string>('0');

  ref: DynamicDialogRef | undefined;

  // Expose lastSavedAt to template (used for image cache-busting via pipe)
  lastSavedAt(): Date | undefined {
    return this.headerService.saveIndicator()?.lastSavedAt ?? undefined;
  }

  // Country list for location select
  countries = [
    { label: 'United States', value: 'United States' },
    { label: 'United Kingdom', value: 'United Kingdom' },
    { label: 'Canada', value: 'Canada' },
    { label: 'Germany', value: 'Germany' },
    { label: 'France', value: 'France' },
    { label: 'Italy', value: 'Italy' },
    { label: 'Spain', value: 'Spain' },
    { label: 'Netherlands', value: 'Netherlands' },
    { label: 'Switzerland', value: 'Switzerland' },
    { label: 'Austria', value: 'Austria' },
    { label: 'Belgium', value: 'Belgium' },
    { label: 'Sweden', value: 'Sweden' },
    { label: 'Norway', value: 'Norway' },
    { label: 'Denmark', value: 'Denmark' },
    { label: 'Finland', value: 'Finland' },
    { label: 'Poland', value: 'Poland' },
    { label: 'Czech Republic', value: 'Czech Republic' },
    { label: 'Hungary', value: 'Hungary' },
    { label: 'Portugal', value: 'Portugal' },
    { label: 'Greece', value: 'Greece' },
    { label: 'Ireland', value: 'Ireland' },
    { label: 'Australia', value: 'Australia' },
    { label: 'New Zealand', value: 'New Zealand' },
    { label: 'Singapore', value: 'Singapore' },
    { label: 'Hong Kong', value: 'Hong Kong' },
    { label: 'Japan', value: 'Japan' },
    { label: 'South Korea', value: 'South Korea' },
    { label: 'United Arab Emirates', value: 'United Arab Emirates' },
    { label: 'Israel', value: 'Israel' },
    { label: 'India', value: 'India' },
    { label: 'Brazil', value: 'Brazil' },
    { label: 'Mexico', value: 'Mexico' },
    { label: 'Argentina', value: 'Argentina' },
    { label: 'Chile', value: 'Chile' },
    { label: 'South Africa', value: 'South Africa' },
  ];

  // Blockchain options loaded from backend (id-based)
  get blockchains() {
    return this.blockchainsLookup.options();
  }

  // Claim platform list (only Mosaic for now)
  claimPlatforms = [{ label: 'Mosaic', value: 'Mosaic' }];

  // Tab-related properties (keep for tabs functionality)
  teamMembers: TeamMember[] = [];
  vestingCategories: VestingCategory[] = [];
  updates: Update[] = [];

  // Chart data and options
  chartData: {
    labels: string[];
    datasets: Array<{
      data: number[];
      backgroundColor: string[];
      borderWidth: number;
    }>;
  } | null = null;
  chartOptions: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    plugins: {
      legend: {
        display: boolean;
      };
    };
  } | null = null;

  // Overview navigation and editors
  overviewNavItems: OverviewNavItem[] = [
    { id: '1', title: 'Our Story' },
    { id: '2', title: 'Problem' },
    { id: '3', title: 'Solution' },
    { id: '4', title: 'Technology' },
    { id: '5', title: 'Market & Audience' },
    { id: '6', title: 'Business Model' },
    { id: '7', title: 'Roadmap' },
  ];
  selectedOverviewIndex: number = 0;

  // Form controls for each overview section (not required)
  ourStoryControl = new FormControl('');
  problemControl = new FormControl('');
  solutionControl = new FormControl('');
  technologyControl = new FormControl('');
  marketAudienceControl = new FormControl('');
  businessModelControl = new FormControl('');
  roadmapControl = new FormControl('');

  // Editor configuration
  editorModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['image'],
    ],
    ImageResize: {
      modules: ['Resize'], // ONLY Resize - no Toolbar or DisplaySize
      // Explicitly disable toolbar
      toolbarStyles: {
        display: 'none',
      },
    },
  };

  // Define individual form controls - ALL REQUIRED
  nameControl = new FormControl('', [Validators.required, Validators.maxLength(30)]);
  descriptionControl = new FormControl('', [Validators.required, Validators.maxLength(600)]);
  categoriesControl = new FormControl<string[]>([], [Validators.required]);

  // Token Information
  tokenNameControl = new FormControl('', [Validators.required]);
  tokenSymbolControl = new FormControl('', [Validators.required, Validators.maxLength(10)]);
  tokenAddressControl = new FormControl('', [Validators.required]);
  totalSupplyControl = new FormControl(null, [Validators.required, Validators.min(1)]);

  // Swap and Claim settings
  swapOnControl = new FormControl<string | null>(null, [Validators.required]);
  claimOnControl = new FormControl<string | null>({ value: 'Mosaic', disabled: true }, [
    Validators.required,
  ]);

  // Price settings
  publicSalePriceControl = new FormControl(null, [Validators.required, Validators.min(0)]);
  privateSalePriceControl = new FormControl(null, [Validators.required, Validators.min(0)]);

  // Sale Information
  softCapControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  hardCapControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  minAllocationControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  maxAllocationControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  totalTokensForSaleControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  poolTypesControl = new FormControl(
    { value: 'Private sale round/Public sale round', disabled: true },
    [Validators.required]
  );
  unsoldTokensControl = new FormControl('', [Validators.required]);

  // Investment Rounds
  privateSaleStartControl = new FormControl<Date | null>(null, [Validators.required]);
  privateSaleEndControl = new FormControl<Date | null>(null, [Validators.required]);
  publicSaleStartControl = new FormControl<Date | null>(null, [Validators.required]);
  publicSaleEndControl = new FormControl<Date | null>(null, [Validators.required]);
  tgeDateControl = new FormControl<Date | null>(null, [Validators.required]);
  tgePriceControl = new FormControl(null, [Validators.required, Validators.min(0)]);

  // Auto-link consecutive dates (when enabled, end dates auto-set to next period's start)
  autoLinkDatesControl = new FormControl(false);
  autoLinkDates = signal(false);

  // Location and verification
  locationControl = new FormControl<string | null>(null, [Validators.required]);
  verifiedControl = new FormControl(false);
  micaCompliantControl = new FormControl(false);

  // Social Links (optional)
  twitterUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  linkedinUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  telegramUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  discordUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);

  // Documentation Links (optional)
  whitepaperUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  tokenomicsUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);

  // Project Owner
  projectOwnerControl = new FormControl<string | null>(null, [Validators.required]); // ObjectId of selected owner
  projectOwnerEmailControl = new FormControl<string>(''); // For autocomplete display
  projectOwnerSuggestions = signal<ProjectOwnerResponseAdminAdminDto[]>([]);
  projectOwnerSearchLoading = signal(false);
  selectedProjectOwner = signal<ProjectOwnerResponseAdminAdminDto | null>(null);

  ownerUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);

  // Project Image
  projectImageControl = new FormControl<File | null>(null);
  projectImagePreview = signal<string | null>(null);
  projectImageError: string | null = null;

  // URL field editing state signals (to hide errors while typing)
  isEditingOwnerUrl = signal(false);
  isEditingTwitterUrl = signal(false);
  isEditingLinkedinUrl = signal(false);
  isEditingTelegramUrl = signal(false);
  isEditingDiscordUrl = signal(false);
  isEditingWhitepaperUrl = signal(false);
  isEditingTokenomicsUrl = signal(false);

  // Temporary maxLength error signals
  showNameMaxLengthError = signal(false);
  showDescriptionMaxLengthError = signal(false);
  private nameMaxLengthTimer: any = null;
  private descriptionMaxLengthTimer: any = null;

  // Debounce subjects for URL field typing
  private urlFieldTyping$ = new Subject<string>();

  // Form will be initialized in constructor
  projectForm!: FormGroup;

  constructor() {
    // Create form group with new structure
    this.projectForm = new FormGroup({
      name: this.nameControl,
      description: this.descriptionControl,
      categoryIds: this.categoriesControl,

      // Token Information
      tokenName: this.tokenNameControl,
      tokenSymbol: this.tokenSymbolControl,
      tokenAddress: this.tokenAddressControl,
      totalSupply: this.totalSupplyControl,

      // Swap and Claim settings
      swapOn: this.swapOnControl,
      claimOn: this.claimOnControl,

      // Price settings
      publicSalePrice: this.publicSalePriceControl,
      privateSalePrice: this.privateSalePriceControl,

      // Sale Information
      softCap: this.softCapControl,
      hardCap: this.hardCapControl,
      minAllocation: this.minAllocationControl,
      maxAllocation: this.maxAllocationControl,
      totalTokensForSale: this.totalTokensForSaleControl,
      poolTypes: this.poolTypesControl,
      unsoldTokens: this.unsoldTokensControl,

      // Investment Rounds
      privateSaleStart: this.privateSaleStartControl,
      privateSaleEnd: this.privateSaleEndControl,
      publicSaleStart: this.publicSaleStartControl,
      publicSaleEnd: this.publicSaleEndControl,
      tgeDate: this.tgeDateControl,
      tgePrice: this.tgePriceControl,

      // Location and verification
      location: this.locationControl,
      verified: this.verifiedControl,
      micaCompliant: this.micaCompliantControl,

      // Project Owner
      projectOwner: this.projectOwnerControl,
      ownerUrl: this.ownerUrlControl,

      // Social Links
      twitterUrl: this.twitterUrlControl,
      linkedinUrl: this.linkedinUrlControl,
      telegramUrl: this.telegramUrlControl,
      discordUrl: this.discordUrlControl,

      // Documentation Links
      whitepaperUrl: this.whitepaperUrlControl,
      tokenomicsUrl: this.tokenomicsUrlControl,

      // Project Image
      projectImage: this.projectImageControl,

      // Overview sections for each editor
      ourStory: this.ourStoryControl,
      problem: this.problemControl,
      solution: this.solutionControl,
      technology: this.technologyControl,
      marketAudience: this.marketAudienceControl,
      businessModel: this.businessModelControl,
      roadmap: this.roadmapControl,

      // Keep tabs-related form arrays for later
      teamMembers: new FormArray([]),
      vestingCategories: new FormArray([]),
      updates: new FormArray([]),
      tokenAllocation: new FormArray([]),
    });

    // Track form validity changes
    this.projectForm.statusChanges.subscribe(() => {
      this.formValid.set(this.projectForm.valid);
    });

    // Subscribe to token allocation changes
    this.tokenAllocationFormArray.valueChanges.subscribe(() => {
      // Always update chart data when FormArray changes, regardless of current tab
      this.forceChartReinitialization();
    });

    // Automatically update header buttons when form validity or submitting state changes
    effect(() => {
      // Only update buttons if header is not in loading state
      if (!this.headerService.loading()) {
        this.updateHeaderButtons();
      }
    });

    // Set up debounced URL field validation
    // After user stops typing for 800ms, hide the "editing" state to show errors
    this.urlFieldTyping$
      .pipe(
        debounceTime(800), // Wait 800ms after user stops typing
        takeUntilDestroyed()
      )
      .subscribe((fieldName) => {
        this.setUrlFieldEditingState(fieldName, false);
      });

    // Set up auto-link dates functionality
    this.setupAutoLinkDates();
  }

  /**
   * Set up auto-link dates functionality
   * When enabled, end dates are automatically set to the start of the next period:
   * - Private Sale End = Public Sale Start
   * - Public Sale End = TGE Date
   */
  private setupAutoLinkDates(): void {
    // Toggle handler for auto-link checkbox
    this.autoLinkDatesControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((enabled) => {
      this.autoLinkDates.set(enabled ?? false);
      if (enabled) {
        this.syncLinkedDates();
      }
    });

    // When auto-link is enabled, sync dates when start dates change
    this.publicSaleStartControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((date) => {
      if (this.autoLinkDates() && date) {
        this.privateSaleEndControl.setValue(date);
        this.privateSaleEndControl.markAsDirty();
      }
    });

    this.tgeDateControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((date) => {
      if (this.autoLinkDates() && date) {
        this.publicSaleEndControl.setValue(date);
        this.publicSaleEndControl.markAsDirty();
      }
    });
  }

  /**
   * Sync all linked dates when auto-link is enabled
   * This ensures end dates match the start of the next period
   */
  private syncLinkedDates(): void {
    const publicSaleStart = this.publicSaleStartControl.value;
    const tgeDate = this.tgeDateControl.value;

    if (publicSaleStart) {
      this.privateSaleEndControl.setValue(publicSaleStart);
      this.privateSaleEndControl.markAsDirty();
    }
    if (tgeDate) {
      this.publicSaleEndControl.setValue(tgeDate);
      this.publicSaleEndControl.markAsDirty();
    }
  }

  /**
   * Ensure linked dates are synced before saving and add them to pending changes
   * Call this before any save operation when autoLinkDates is enabled
   */
  private ensureLinkedDatesSync(): void {
    if (this.autoLinkDates()) {
      const publicSaleStart = this.publicSaleStartControl.value;
      const tgeDate = this.tgeDateControl.value;

      // Sync private sale end to public sale start
      if (publicSaleStart) {
        this.privateSaleEndControl.setValue(publicSaleStart, { emitEvent: false });
        // Explicitly add to pending changes to ensure it's saved
        this.pendingChanges.set('privateSaleEnd', publicSaleStart);
      }

      // Sync public sale end to TGE date
      if (tgeDate) {
        this.publicSaleEndControl.setValue(tgeDate, { emitEvent: false });
        // Explicitly add to pending changes to ensure it's saved
        this.pendingChanges.set('publicSaleEnd', tgeDate);
      }
    }
  }

  /**
   * Detect if dates are already linked and auto-enable the checkbox
   * Called after loading project data to restore the checkbox state
   */
  private detectLinkedDates(): void {
    const privateSaleEnd = this.privateSaleEndControl.value;
    const publicSaleStart = this.publicSaleStartControl.value;
    const publicSaleEnd = this.publicSaleEndControl.value;
    const tgeDate = this.tgeDateControl.value;

    // Check if both conditions for linked dates are met
    const isPrivateEndLinked = this.areDatesEqual(privateSaleEnd, publicSaleStart);
    const isPublicEndLinked = this.areDatesEqual(publicSaleEnd, tgeDate);

    // If both links match, enable the checkbox (without emitting to avoid triggering save)
    if (isPrivateEndLinked && isPublicEndLinked) {
      this.autoLinkDatesControl.setValue(true, { emitEvent: false });
      this.autoLinkDates.set(true);
    }
  }

  /**
   * Compare two dates for equality (ignoring milliseconds differences)
   */
  private areDatesEqual(date1: Date | null, date2: Date | null): boolean {
    if (!date1 || !date2) {
      return false;
    }
    // Compare timestamps rounded to seconds to handle minor differences
    return Math.floor(date1.getTime() / 1000) === Math.floor(date2.getTime() / 1000);
  }

  /**
   * Get error message for a form control
   */
  getErrorMessage(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors) {
      return '';
    }

    const errors = control.errors;

    if (errors['required']) {
      return this.translateService.instant('projectEdit.requiredField', { field: fieldName });
    }

    if (errors['minlength']) {
      return this.translateService.instant('projectEdit.minLengthError', {
        field: fieldName,
        min: errors['minlength'].requiredLength,
      });
    }

    if (errors['maxlength']) {
      return this.translateService.instant('projectEdit.maxLengthError', {
        field: fieldName,
        max: errors['maxlength'].requiredLength,
      });
    }

    if (errors['min']) {
      return this.translateService.instant('projectEdit.minValueError', {
        field: fieldName,
        min: errors['min'].min,
      });
    }

    if (errors['pattern']) {
      return this.translateService.instant('projectEdit.invalidFormat', { field: fieldName });
    }

    return this.translateService.instant('projectEdit.invalidField', { field: fieldName });
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = this.projectId !== null && this.projectId !== 'new';

    // For new projects, enable auto-link dates by default
    if (!this.isEditMode) {
      this.autoLinkDatesControl.setValue(true);
      this.autoLinkDates.set(true);
    }

    // Show header skeletons while loading
    this.headerService.setLoading(true);

    // Setup static header content (title and subtitle)
    this.setupHeader();

    // Setup auto-save functionality
    this.setupAutoSave();

    // Prime cache of blockchains for selects and display pipes
    this.blockchainsLookup.load();

    // Load initial project owners for autocomplete
    this.loadInitialProjectOwners();

    // Load data
    this.loadData();

    // Initialize chart data structure early
    this.forceChartReinitialization();
  }

  ngAfterViewInit(): void {
    // Initialize chart after view init to ensure canvas is ready
    setTimeout(() => {
      if (this.chartData && this.chartData.labels && this.chartData.labels.length > 0) {
        this.forceChartReinitialization();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.headerService.clear();
    this.headerService.setSaveIndicator(null);
  }

  /**
   * Prevent browser close/refresh if there are unsaved changes
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    const saveIndicator = this.headerService.saveIndicator();

    // Show warning if not in saved state (includes unsaved and saving states)
    if (saveIndicator && saveIndicator.status !== 'saved') {
      event.preventDefault();
      event.returnValue = ''; // Modern browsers ignore custom message
    }
  }

  /**
   * Setup static header content (title and subtitle)
   * Called once during ngOnInit
   */
  private setupHeader(): void {
    if (this.isEditMode) {
      this.headerService.setTitle(this.translateService.instant('projectEdit.editTitle'));
      this.headerService.setSubtitle(this.translateService.instant('projectEdit.editSubtitle'));
    } else {
      this.headerService.setTitle(this.translateService.instant('projectEdit.addTitle'));
      this.headerService.setSubtitle(
        this.translateService.instant('projectEdit.addProjectSubtitle')
      );
    }
  }

  /**
   * Update header action buttons based on current state
   * Automatically called via effect() when signals change
   */
  private updateHeaderButtons(): void {
    const status = this.projectStatus();
    const isPublished = status === 'published';
    const isPendingApproval = status === 'pending_approval';
    const isFeatured = this.projectFeatured();

    const buttons: any[] = [
      {
        label: this.translateService.instant('common.goBack'),
        icon: 'pi pi-arrow-left',
        severity: 'secondary',
        outlined: true,
        action: () => this.onCancel(),
      },
      {
        label: this.translateService.instant('common.delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        outlined: true,
        disabled: !this.isEditMode,
        loading: this.submitting(),
        action: () => this.onDelete(),
      },
    ];

    // Add Approve/Reject buttons for pending_approval status
    if (isPendingApproval && this.projectId) {
      buttons.push({
        label: this.translateService.instant('common.reject'),
        icon: 'pi pi-times',
        severity: 'danger',
        outlined: true,
        disabled: this.submitting(),
        loading: this.submitting(),
        action: () => this.onReject(),
      });
      buttons.push({
        label: this.translateService.instant('common.approve'),
        icon: 'pi pi-check',
        severity: 'success',
        disabled: this.submitting(),
        loading: this.submitting(),
        action: () => this.onApprove(),
      });
    }

    // Add Feature button (only for published projects)
    buttons.push({
      label: this.translateService.instant(isFeatured ? 'common.unfeature' : 'common.feature'),
      icon: isFeatured ? 'pi pi-star-fill' : 'pi pi-star',
      severity: isFeatured ? 'help' : 'info',
      disabled: !isPublished || !this.projectId,
      loading: this.submitting(),
      action: () => this.onFeatureToggle(),
    });

    // Check if there are unsaved changes
    const saveIndicator = this.headerService.saveIndicator();
    const hasUnsavedChanges =
      this.pendingChanges.size > 0 || (saveIndicator && saveIndicator.status !== 'saved');

    // Add Publish button - always visible for admins, disabled if form invalid, no project ID, or unsaved changes
    buttons.push({
      label: this.translateService.instant(isPublished ? 'common.unpublish' : 'common.publish'),
      icon: isPublished ? 'pi pi-eye-slash' : 'pi pi-check',
      severity: isPublished ? 'danger' : 'success',
      disabled: !this.formValid() || !this.projectId || this.submitting() || hasUnsavedChanges,
      loading: this.submitting(),
      action: () => this.onPublishToggle(),
    });

    // Add Save button
    buttons.push({
      label: this.translateService.instant('common.save'),
      icon: 'pi pi-save',
      severity: 'primary',
      disabled: this.autoSaving() || this.submitting() || this.hasInvalidTouchedFields(),
      loading: this.autoSaving(),
      action: () => this.manualSave(),
    });

    this.headerService.setButtons(buttons);
  }

  /**
   * Setup auto-save functionality for form fields
   * Monitors form changes and automatically saves to backend with debouncing
   * Accumulates all changes during debounce period and saves them together
   */
  private setupAutoSave(): void {
    // Setup the auto-save pipeline with debouncing and accumulation
    this.autoSaveSubject
      .pipe(
        // Accumulate changes as they come in
        tap(({ field, value }) => {
          // Store each change in the pending changes map
          this.pendingChanges.set(field, value);

          // Set indicator to unsaved when changes are pending
          this.headerService.setSaveIndicator({ status: 'unsaved' });
        }),
        // Wait for grace period after last change
        debounceTime(this.AUTO_SAVE_DEBOUNCE_MS),
        // Filter out if already saving or if main submit is in progress
        filter(() => !this.submitting() && !this.autoSaving()),
        // Check minimum interval between saves
        filter(() => {
          const now = Date.now();
          const timeSinceLastSave = now - this.lastAutoSaveTime;
          return timeSinceLastSave >= this.AUTO_SAVE_MIN_INTERVAL_MS;
        }),
        // Filter out if no changes accumulated
        filter(() => this.pendingChanges.size > 0),
        // Disable auto-save execution for published projects (changes still tracked above)
        filter(() => this.projectStatus() !== 'published'),
        // Switch to save operation (cancels previous if new change comes)
        switchMap(() => {
          // Execute the shared save logic
          this.executeSave();
          // Return observable for the pipeline
          return of(null);
        }),
        // Automatically unsubscribe when component is destroyed
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Subscribe to form changes for auto-save (exclude nested arrays)
    this.subscribeToFormChangesForAutoSave();
  }

  /**
   * Subscribe to individual form control changes for auto-save
   * Excludes FormArrays (teamMembers, vestingCategories, updates, tokenAllocation)
   * as they have their own dedicated endpoints
   */
  private subscribeToFormChangesForAutoSave(): void {
    // Get all form control names except the FormArrays
    const excludedControls = ['teamMembers', 'vestingCategories', 'updates', 'tokenAllocation'];

    Object.keys(this.projectForm.controls).forEach((controlName) => {
      if (excludedControls.includes(controlName)) {
        return; // Skip FormArrays
      }

      const control = this.projectForm.get(controlName);
      if (control) {
        control.valueChanges
          .pipe(
            // Only emit when auto-save is enabled (after initial load)
            filter(() => this.isAutoSaveEnabled),
            // Only save if the field is valid (prevent saving invalid data)
            filter(() => control.valid),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe((value) => {
            // Emit change to auto-save subject
            this.autoSaveSubject.next({ field: controlName, value });
          });
      }
    });

    // Subscribe to form status changes to update header buttons
    // This ensures the save button state updates when validation changes
    this.projectForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateHeaderButtons();
    });
  }

  /**
   * Load all necessary data
   */
  private async loadData(): Promise<void> {
    try {
      // Load categories in parallel
      const categoriesPromise = this.loadCategories();

      // Load project data if in edit mode
      if (this.isEditMode && this.projectId) {
        await Promise.all([this.loadProject(this.projectId), categoriesPromise]);
      } else {
        // Still load categories for new projects
        await categoriesPromise;

        // Enable auto-save for new projects immediately
        // (no need to wait for data loading since there's no data to load)
        setTimeout(() => {
          this.isAutoSaveEnabled = true;
          console.log('Auto-save enabled for new project');
        }, 500);
      }
    } finally {
      // Hide skeletons and show buttons
      this.headerService.setLoading(false);
      this.loading.set(false);
      this.categoriesLoading.set(false);
    }
  }

  /**
   * Load categories from the API
   */
  private async loadCategories(): Promise<void> {
    try {
      this.categoriesLoading.set(true);
      const response = await this.api.invoke(adminCategoriesControllerListCategoriesV1, {});
      this.categories = response || [];
    } catch (error) {
      console.error('Failed to load categories:', error);
      this.categories = [];
    } finally {
      this.categoriesLoading.set(false);
    }
  }

  /**
   * Manually trigger save - bypasses debounce and forces immediate save
   * If there are pending changes, saves them immediately
   * @param nextPanel - Optional next panel value to navigate to after successful save
   */
  manualSave(nextPanel?: string): void {
    // If already saving, don't trigger another save
    if (this.autoSaving() || this.submitting()) {
      return;
    }

    // If there are no pending changes, show message that everything is already saved
    if (this.pendingChanges.size === 0) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('common.info'),
        detail: this.translateService.instant('common.noChangesToSave'),
      });
      // Still navigate to next panel if requested
      if (nextPanel) {
        this.navigateToPanel(nextPanel);
      }
      return;
    }

    // Execute the save immediately and navigate on success
    this.executeSave(nextPanel);
  }

  /**
   * Navigate to a specific accordion panel by setting the accordion value
   */
  private navigateToPanel(panelValue: string): void {
    // Update the accordion value signal
    this.accordionValue.set(panelValue);
  }

  /**
   * Check if Panel 0 (Project Information) fields are valid
   * Only checks touched/dirty fields to allow partial saves
   */
  isPanel0Valid(): boolean {
    const fields = [
      'name',
      'ownerUrl',
      'description',
      'categoryIds',
      'location',
      'verified',
      'micaCompliant',
      'twitterUrl',
      'linkedinUrl',
      'telegramUrl',
      'discordUrl',
      'whitepaperUrl',
      'tokenomicsUrl',
    ];
    return fields.every((field) => {
      const control = this.projectForm.get(field);
      if (!control) return true;
      // Only validate if field has been touched or is dirty
      if (control.touched || control.dirty) {
        return control.valid;
      }
      return true; // Untouched fields are considered valid
    });
  }

  /**
   * Check if any touched or dirty field in the entire form is invalid
   * Used for the header save button
   */
  hasInvalidTouchedFields(): boolean {
    const allFields = Object.keys(this.projectForm.controls);
    return allFields.some((field) => {
      const control = this.projectForm.get(field);
      if (!control) return false;
      // Check if field is touched/dirty AND invalid
      if ((control.touched || control.dirty) && !control.valid) {
        return true;
      }
      return false;
    });
  }

  /**
   * Check if Panel 1 (Token Information) fields are valid
   * Only checks touched/dirty fields to allow partial saves
   */
  isPanel1Valid(): boolean {
    const fields = ['tokenName', 'tokenSymbol', 'tokenAddress', 'totalSupply', 'swapOn', 'claimOn'];
    return fields.every((field) => {
      const control = this.projectForm.get(field);
      if (!control) return true;
      // Only validate if field has been touched or is dirty
      if (control.touched || control.dirty) {
        return control.valid;
      }
      return true; // Untouched fields are considered valid
    });
  }

  /**
   * Check if Panel 2 (Sale Information) fields are valid
   * Only checks touched/dirty fields to allow partial saves
   */
  isPanel2Valid(): boolean {
    const fields = [
      'softCap',
      'hardCap',
      'minAllocation',
      'maxAllocation',
      'totalTokensForSale',
      'unsoldTokens',
    ];
    return fields.every((field) => {
      const control = this.projectForm.get(field);
      if (!control) return true;
      // Only validate if field has been touched or is dirty
      if (control.touched || control.dirty) {
        return control.valid;
      }
      return true; // Untouched fields are considered valid
    });
  }

  /**
   * Check if Panel 3 (Investment Rounds) fields are valid
   * Only checks touched/dirty fields to allow partial saves
   */
  isPanel3Valid(): boolean {
    const fields = [
      'privateSalePrice',
      'privateSaleStart',
      'privateSaleEnd',
      'publicSalePrice',
      'publicSaleStart',
      'publicSaleEnd',
      'tgePrice',
      'tgeDate',
    ];
    return fields.every((field) => {
      const control = this.projectForm.get(field);
      if (!control) return true;
      // Only validate if field has been touched or is dirty
      if (control.touched || control.dirty) {
        return control.valid;
      }
      return true; // Untouched fields are considered valid
    });
  }

  /**
   * Check if Panel 4 (Additional Content - Tabs) fields are valid
   * Includes overview sections, team members, vesting categories, and updates
   * Only checks touched/dirty fields to allow partial saves
   */
  isPanel4Valid(): boolean {
    const fields = [
      'ourStory',
      'problem',
      'solution',
      'technology',
      'marketAudience',
      'businessModel',
      'roadmap',
      'teamMembers',
      'vestingCategories',
      'tokenAllocation',
      'updates',
    ];
    return fields.every((field) => {
      const control = this.projectForm.get(field);
      if (!control) return true;
      // Only validate if field has been touched or is dirty
      if (control.touched || control.dirty) {
        return control.valid;
      }
      return true; // Untouched fields are considered valid
    });
  }

  /**
   * Execute save operation with current pending changes
   * Shared logic used by both autosave and manual save
   * @param nextPanel - Optional next panel to navigate to after successful save
   */
  private executeSave(nextPanel?: string): void {
    // Ensure linked dates are synced before saving
    this.ensureLinkedDatesSync();

    this.autoSaving.set(true);
    this.headerService.setSaveIndicator({ status: 'saving' });
    this.lastAutoSaveTime = Date.now();

    // Prepare partial update with ALL accumulated changes (only valid fields)
    const partialUpdate: Record<string, unknown> = {};

    // Process all pending changes and verify field validity
    this.pendingChanges.forEach((value, field) => {
      const control = this.projectForm.get(field);

      // Skip projectImage field entirely from autosave
      if (field === 'projectImage') {
        return;
      }

      // Double-check: only include if the field is still valid
      if (!control || !control.valid) {
        console.log(`Skipping invalid field from save: ${field}`);
        return;
      }

      // Convert date fields to ISO strings if needed
      if (
        field.includes('Date') ||
        field.includes('Start') ||
        field.includes('End') ||
        field.includes('On')
      ) {
        partialUpdate[field] = value instanceof Date ? value.toISOString() : value;
      } else {
        partialUpdate[field] = value;
      }
    });

    // Clear pending changes after preparing the update
    this.pendingChanges.clear();

    // Skip save if no valid fields to update
    if (Object.keys(partialUpdate).length === 0) {
      console.log('No valid fields to save');
      this.autoSaving.set(false);
      return;
    }

    console.log('Saving fields:', Object.keys(partialUpdate));

    // Determine if we need to create or update
    const savePromise =
      !this.isEditMode || !this.projectId
        ? this.api
            .invoke(adminProjectsControllerCreateProjectV1, {
              body: partialUpdate as any,
            })
            .then((response) => {
              // Update component state with the new project ID
              if (response && response.id) {
                this.projectId = response.id;
                this.isEditMode = true;
                this.projectStatus.set(response.status);

                // Update the URL to reflect the new project ID (without page reload)
                this.router.navigate(['/projects', response.id], { replaceUrl: true });

                // Update header to show "Edit Project" instead of "Add Project"
                this.headerService.setTitle(this.translateService.instant('projectEdit.editTitle'));
                this.headerService.setSubtitle(
                  this.translateService.instant('projectEdit.editSubtitle')
                );

                // Update header buttons to reflect edit mode
                this.updateHeaderButtons();

                console.log('Project created with ID:', response.id);
              }
              return response;
            })
        : this.api.invoke(adminProjectsControllerUpdateProjectV1, {
            id: this.projectId!,
            body: partialUpdate as any,
          });

    // Handle the response
    savePromise
      .then((response) => {
        this.autoSaving.set(false);

        if (response) {
          // Update save indicator with timestamp from API response
          if (response.updatedAt) {
            this.headerService.setSaveIndicator({
              status: 'saved',
              lastSavedAt: new Date(response.updatedAt),
            });
          }

          // If backend returned a name and our form name is empty, set it
          if (response.name && !this.nameControl.value) {
            this.nameControl.setValue(response.name, { emitEvent: false });
            console.log('Set default project name from backend:', response.name);
          }

          console.log('Save successful');

          // Navigate to next panel if requested (manual save with navigation)
          if (nextPanel) {
            this.navigateToPanel(nextPanel);
          }
        }
      })
      .catch((error) => {
        this.autoSaving.set(false);
        console.error('Save failed:', error);
      });
  }

  /**
   * Load project data from the backend
   */
  private async loadProject(projectId: string): Promise<void> {
    try {
      // Ensure blockchains cache is available so the select can show label for existing id
      this.blockchainsLookup.load();

      // Call the API to get project data
      const project: ProjectResponseAdminDto = await this.api.invoke(
        adminProjectsControllerGetProjectV1,
        { id: projectId }
      );
      console.log('Loaded project data:', project);
      console.log('ProjectOwner from backend:', project.projectOwner);

      // Set project status
      this.projectStatus.set(project.status);

      // Set project featured flag
      this.projectFeatured.set(project.featured || false);

      // Set save indicator with last update timestamp
      if (project.updatedAt) {
        this.headerService.setSaveIndicator({
          status: 'saved',
          lastSavedAt: new Date(project.updatedAt),
        });
      }

      // Set project owner if exists (already populated by backend)
      if (project.projectOwner) {
        const owner = project.projectOwner as any;
        console.log('Project owner from backend:', owner);

        if (typeof owner === 'object' && owner.email) {
          // ProjectOwner is populated object
          const ownerData: ProjectOwnerResponseAdminAdminDto = {
            id: owner.id || owner._id,
            email: owner.email,
            username: owner.username,
            fullName: owner.fullName,
            company: owner.company,
            createdAt: owner.createdAt,
            updatedAt: owner.updatedAt,
            isBanned: owner.isBanned || false,
            banReason: owner.banReason,
            bannedAt: owner.bannedAt,
            projectCount: owner.projectCount || 0,
          };

          this.selectedProjectOwner.set(ownerData);
          this.projectOwnerEmailControl.setValue(owner.email);
          console.log('Set selected owner:', ownerData);
        } else {
          // ProjectOwner is just ID string (shouldn't happen with populate)
          this.projectOwnerControl.setValue(project.projectOwner as string);
          console.log('Project owner is just ID:', project.projectOwner);
        }
      }

      // Populate form with project data using spread operator
      // Only need to handle date conversions separately
      // Exclude poolTypes from patch - it should remain as the static default value
      const { poolTypes, ...projectData } = project;
      this.projectForm.patchValue({
        ...projectData,
        // Convert ISO date strings to Date objects for DatePicker components
        privateSaleStart: project.privateSaleStart ? new Date(project.privateSaleStart) : null,
        privateSaleEnd: project.privateSaleEnd ? new Date(project.privateSaleEnd) : null,
        publicSaleStart: project.publicSaleStart ? new Date(project.publicSaleStart) : null,
        publicSaleEnd: project.publicSaleEnd ? new Date(project.publicSaleEnd) : null,
        tgeDate: project.tgeDate ? new Date(project.tgeDate) : null,
      });

      // Detect if dates are linked and auto-enable the checkbox
      this.detectLinkedDates();

      // Handle project image if available
      if (project.projectImage) {
        this.projectImagePreview.set(project.projectImage);
      }

      // Load nested arrays data - Clear existing arrays
      this.teamMembersFormArray.clear();
      this.vestingCategoriesFormArray.clear();
      this.updatesFormArray.clear();
      this.tokenAllocationFormArray.clear();

      // Load team members with image preview handling
      (project.teamMembers as TeamMember[] | undefined)?.forEach((member) => {
        this.teamMembersFormArray.push(this.createFormGroupFromData(member));
        this.teamMembers.push({
          ...member,
          image: null,
          imagePreview: typeof member.image === 'string' ? member.image : undefined,
        });
      });

      // Load vesting categories
      (project.vestingCategories as VestingCategory[] | undefined)?.forEach((category) => {
        this.vestingCategoriesFormArray.push(this.createFormGroupFromData(category));
        this.vestingCategories.push(category);
      });

      // Load updates with date and image preview handling
      (project.updates as Update[] | undefined)?.forEach((update) => {
        this.updatesFormArray.push(
          this.createFormGroupFromData({
            ...update,
            publishDate: update.publishDate ? new Date(update.publishDate) : null,
          })
        );
        this.updates.push({
          ...update,
          publishDate: update.publishDate ? new Date(update.publishDate) : new Date(),
          coverImage: null,
          coverImagePreview: typeof update.coverImage === 'string' ? update.coverImage : undefined,
        });
      });

      // Load token allocation
      (project.tokenAllocation as TokenAllocation[] | undefined)?.forEach((allocation) => {
        this.tokenAllocationFormArray.push(this.createFormGroupFromData(allocation));
      });

      // Force chart update after loading allocations
      if (project.tokenAllocation?.length) {
        setTimeout(() => this.forceChartReinitialization(), 10);
      }

      // Enable auto-save after initial data is loaded
      // Use setTimeout to ensure form is fully settled
      setTimeout(() => {
        this.isAutoSaveEnabled = true;
        console.log('Auto-save enabled for project:', projectId);
      }, 500);
    } catch (error) {
      // Error already displayed by interceptor
      // Navigate back to list if project not found
      this.router.navigate(['/projects']);
    }
  }

  /**
   * Helper method to create a FormGroup from data object
   * Automatically creates FormControls for all properties in the data
   */
  private createFormGroupFromData<T extends object>(data: T): FormGroup {
    const controls: { [key: string]: FormControl } = {};

    // Create a FormControl for each property in the data object
    Object.keys(data).forEach((key) => {
      controls[key] = new FormControl((data as Record<string, unknown>)[key]);
    });

    return new FormGroup(controls);
  }

  // Keep only the tab-related form arrays
  // Getter for team members FormArray
  get teamMembersFormArray(): FormArray {
    return this.projectForm.get('teamMembers') as FormArray;
  }

  // Getter for vesting categories FormArray
  get vestingCategoriesFormArray(): FormArray {
    return this.projectForm.get('vestingCategories') as FormArray;
  }

  // Getter for updates FormArray
  get updatesFormArray(): FormArray {
    return this.projectForm.get('updates') as FormArray;
  }

  // Getter for token allocation FormArray
  get tokenAllocationFormArray(): FormArray {
    return this.projectForm.get('tokenAllocation') as FormArray;
  }

  protected async onSubmit(): Promise<void> {
    if (this.projectForm.valid && !this.submitting()) {
      this.submitting.set(true);

      try {
        // Get form value - it already matches backend structure
        const formValue = this.projectForm.value;

        // Convert Date objects to ISO strings for dates
        const projectData: any = {
          ...formValue,
          privateSaleStart: formValue.privateSaleStart?.toISOString(),
          privateSaleEnd: formValue.privateSaleEnd?.toISOString(),
          publicSaleStart: formValue.publicSaleStart?.toISOString(),
          publicSaleEnd: formValue.publicSaleEnd?.toISOString(),
          tgeDate: formValue.tgeDate?.toISOString(),
          // Convert updates dates
          updates: formValue.updates?.map((update: Update) => ({
            ...update,
            publishDate:
              update.publishDate instanceof Date
                ? update.publishDate.toISOString()
                : update.publishDate,
          })),
        };

        // If projectImage is not a File object, remove it from submission
        // (it's either null or already uploaded URL string)
        if (!(projectData.projectImage instanceof File)) {
          delete projectData.projectImage;
        }

        if (this.isEditMode && this.projectId) {
          // Update existing project
          await this.api.invoke(adminProjectsControllerUpdateProjectV1, {
            id: this.projectId,
            body: projectData,
          });

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.success.editTitle'),
            detail: this.translateService.instant('projectEdit.success.editMessage'),
            life: 4000,
          });
        } else {
          // Create new project
          await this.api.invoke(adminProjectsControllerCreateProjectV1, {
            body: projectData,
          });

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.success.createTitle'),
            detail: this.translateService.instant('projectEdit.success.createMessage'),
            life: 4000,
          });
        }

        // Navigate back to projects page
        this.router.navigate(['/projects']);
      } catch (error) {
        // Error already displayed by interceptor
        // Just prevent the error from bubbling up
        console.error('Submit error:', error);
      } finally {
        this.submitting.set(false);
      }
    } else if (!this.projectForm.valid) {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched();
    }
  }

  protected async onPublishToggle(): Promise<void> {
    if (!this.projectId) {
      return;
    }

    const isPublished = this.projectStatus() === 'published';
    const isApproved = this.projectStatus() === 'approved';

    // If publishing an unapproved project, show confirmation dialog
    if (!isPublished && !isApproved) {
      this.confirmationService.confirm({
        key: 'publishConfirm',
        message: this.translateService.instant('projectEdit.publish.confirmUnapproved'),
        header: this.translateService.instant('projectEdit.publish.confirmHeader'),
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-success',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          this.executePublishToggle(isPublished);
        },
      });
    } else {
      // For unpublishing or publishing approved projects, proceed directly
      this.executePublishToggle(isPublished);
    }
  }

  /**
   * Execute the actual publish/unpublish API call
   * Extracted to be reusable by both direct calls and confirmation dialog
   */
  private async executePublishToggle(isPublished: boolean): Promise<void> {
    this.submitting.set(true);

    try {
      if (isPublished) {
        // Unpublish the project
        await this.api.invoke(adminProjectsControllerUnpublishProjectV1, {
          id: this.projectId!,
        });

        this.projectStatus.set('draft');

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('projectEdit.success.unpublishTitle'),
          detail: this.translateService.instant('projectEdit.success.unpublishMessage'),
          life: 4000,
        });
      } else {
        // Publish the project
        await this.api.invoke(adminProjectsControllerPublishProjectV1, {
          id: this.projectId!,
        });

        this.projectStatus.set('published');

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('projectEdit.success.publishTitle'),
          detail: this.translateService.instant('projectEdit.success.publishMessage'),
          life: 4000,
        });
      }

      // Update header buttons to reflect new status
      this.updateHeaderButtons();
    } catch (error) {
      // Error already displayed by interceptor
    } finally {
      this.submitting.set(false);
    }
  }

  protected async onApprove(): Promise<void> {
    if (!this.projectId) {
      return;
    }

    // Show confirmation dialog
    this.confirmationService.confirm({
      message: this.translateService.instant('projectEdit.approval.confirmApprove'),
      header: this.translateService.instant('projectEdit.approval.approveProject'),
      icon: 'pi pi-check',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        this.submitting.set(true);

        try {
          const response = await this.api.invoke(adminProjectsControllerApproveProjectV1, {
            id: this.projectId!,
            body: {
              autoPublish: false, // Admin can manually publish later
            },
          });

          this.projectStatus.set(response.status);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.approval.approveSuccess'),
            detail: this.translateService.instant('projectEdit.approval.approveSuccessMessage'),
            life: 5000,
          });

          // Update header buttons to reflect new status
          this.updateHeaderButtons();
        } catch (error) {
          // Error already displayed by interceptor
        } finally {
          this.submitting.set(false);
        }
      },
    });
  }

  protected async onReject(): Promise<void> {
    if (!this.projectId) {
      return;
    }

    // Show confirmation dialog
    this.confirmationService.confirm({
      message: this.translateService.instant('projectEdit.approval.confirmReject'),
      header: this.translateService.instant('projectEdit.approval.rejectProject'),
      icon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        this.submitting.set(true);

        try {
          const response = await this.api.invoke(adminProjectsControllerRejectProjectV1, {
            id: this.projectId!,
          });

          this.projectStatus.set(response.status);

          this.messageService.add({
            severity: 'info',
            summary: this.translateService.instant('projectEdit.approval.rejectSuccess'),
            detail: this.translateService.instant('projectEdit.approval.rejectSuccessMessage'),
            life: 5000,
          });

          // Update header buttons to reflect new status
          this.updateHeaderButtons();
        } catch (error) {
          // Error already displayed by interceptor
        } finally {
          this.submitting.set(false);
        }
      },
    });
  }

  protected async onFeatureToggle(): Promise<void> {
    if (!this.projectId) {
      return;
    }

    this.submitting.set(true);

    try {
      const isFeatured = this.projectFeatured();

      if (isFeatured) {
        // Unfeature the project
        await this.api.invoke(adminProjectsControllerUnfeatureProjectV1, {
          id: this.projectId,
        });

        this.projectFeatured.set(false);

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('projectEdit.success.unfeatureTitle'),
          detail: this.translateService.instant('projectEdit.success.unfeatureMessage'),
          life: 4000,
        });
      } else {
        // Feature the project
        await this.api.invoke(adminProjectsControllerFeatureProjectV1, {
          id: this.projectId,
        });

        this.projectFeatured.set(true);

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('projectEdit.success.featureTitle'),
          detail: this.translateService.instant('projectEdit.success.featureMessage'),
          life: 4000,
        });
      }

      // Update header buttons to reflect new status
      this.updateHeaderButtons();
    } catch (error) {
      // Error already displayed by interceptor
    } finally {
      this.submitting.set(false);
    }
  }

  protected onDelete(): void {
    if (!this.projectId) {
      return;
    }

    // Get the project name from the form for the confirmation dialog
    const projectName = this.nameControl.value || 'this project';

    this.confirmationService.confirm({
      message: this.translateService.instant('projects.confirmDelete.message', {
        name: projectName,
      }),
      header: this.translateService.instant('projects.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('projects.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('projects.confirmDelete.cancel'),
      accept: () => {
        this.submitting.set(true);

        this.api
          .invoke(adminProjectsControllerDeleteProjectV1, { id: this.projectId! })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('projects.success.deleteTitle'),
              detail: this.translateService.instant('projects.success.deleteMessage', {
                name: projectName,
              }),
              life: 4000,
            });

            // Navigate back to projects list
            this.router.navigate(['/projects']);
          })
          .catch(() => {
            // Error already displayed by interceptor
          })
          .finally(() => {
            this.submitting.set(false);
          });
      },
    });
  }

  protected onCancel(): void {
    const saveIndicator = this.headerService.saveIndicator();

    // Prevent navigation if there are unsaved changes
    if (saveIndicator && saveIndicator.status !== 'saved') {
      const confirmed = confirm(
        this.translateService.instant('projectEdit.saveInProgress.message')
      );
      if (!confirmed) {
        return;
      }
    }
    this.router.navigate(['/projects']);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.projectForm.controls).forEach((key) => {
      this.projectForm.get(key)?.markAsTouched();
    });
  }

  // Error message methods
  getNameErrorMessage(): string {
    if (this.nameControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.nameRequired');
    }
    if (this.nameControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.nameMinLength');
    }
    return '';
  }

  getDescriptionErrorMessage(): string {
    if (this.descriptionControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.descriptionRequired');
    }
    if (this.descriptionControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.descriptionMinLength');
    }
    return '';
  }

  getTokenNameErrorMessage(): string {
    if (this.tokenNameControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.tokenNameRequired');
    }
    return '';
  }

  getTokenSymbolErrorMessage(): string {
    if (this.tokenSymbolControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.tokenSymbolRequired');
    }
    return '';
  }

  getSoftCapErrorMessage(): string {
    if (this.softCapControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.softCapRequired');
    }
    if (this.softCapControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.softCapMin');
    }
    return '';
  }

  getHardCapErrorMessage(): string {
    if (this.hardCapControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.hardCapRequired');
    }
    if (this.hardCapControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.hardCapMin');
    }
    return '';
  }

  getLocationErrorMessage(): string {
    if (this.locationControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.locationRequired');
    }
    return '';
  }

  getSwapOnErrorMessage(): string {
    if (this.swapOnControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.swapOnRequired');
    }
    return '';
  }

  getClaimOnErrorMessage(): string {
    if (this.claimOnControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.claimOnRequired');
    }
    return '';
  }

  // Methods needed for tabs functionality
  onOverviewNavSelected(index: number): void {
    this.selectedOverviewIndex = index;
    // Scroll to the corresponding editor
    this.scrollToOverviewSection(index);
  }

  // Method to handle scrolling to specific overview section
  scrollToOverviewSection(index: number): void {
    if (index !== null && index !== undefined) {
      const element = document.getElementById(`overview-section-${index}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }
  }

  // Get the current editor control based on selected index
  getCurrentEditorControl(): FormControl {
    switch (this.selectedOverviewIndex) {
      case 0:
        return this.ourStoryControl;
      case 1:
        return this.problemControl;
      case 2:
        return this.solutionControl;
      case 3:
        return this.technologyControl;
      case 4:
        return this.marketAudienceControl;
      case 5:
        return this.businessModelControl;
      case 6:
        return this.roadmapControl;
      default:
        return this.ourStoryControl;
    }
  }

  // Team members methods
  openAddTeamMemberDialog(): void {
    this.ref = this.dialogService.open(TeamMemberDialogComponent, {
      header: this.translateService.instant('projectEdit.teamMembers.addMember'),
      width: '600px',
      modal: true,
      closable: true,
      data: { projectId: this.projectId },
    });

    this.ref.onClose.subscribe((teamMember: TeamMember) => {
      if (teamMember) {
        this.addTeamMember(teamMember);
      }
    });
  }

  openEditTeamMemberDialog(member: TeamMember, index: number): void {
    this.ref = this.dialogService.open(TeamMemberDialogComponent, {
      header: this.translateService.instant('projectEdit.teamMembers.editMember'),
      width: '600px',
      modal: true,
      closable: true,
      data: { teamMember: member, projectId: this.projectId },
    });

    this.ref.onClose.subscribe((updatedMember: TeamMember) => {
      if (updatedMember) {
        this.updateTeamMember(updatedMember, index);
      }
    });
  }

  onEditMember(event: { member: TeamMember; index: number }): void {
    this.openEditTeamMemberDialog(event.member, event.index);
  }

  async addTeamMember(teamMember: TeamMember): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot add team member: No project ID available');
      return;
    }

    try {
      // Send multipart to backend; include file only if provided
      const response = await this.api.invoke(adminProjectsControllerAddOrUpdateTeamMemberV1, {
        projectId: this.projectId,
        body: {
          name: teamMember.name,
          position: teamMember.position,
          linkedinUrl: teamMember.linkedinUrl,
          description: teamMember.description,
          file: teamMember.image instanceof File ? (teamMember.image as Blob) : undefined,
        } as any,
      });

      // Only add to UI if save was successful
      if (response.teamMembers && Array.isArray(response.teamMembers)) {
        const newMember = (response.teamMembers as TeamMember[]).find(
          (member) => member.name === teamMember.name
        );

        if (newMember && newMember.id) {
          // Update with real ID from backend
          teamMember.id = newMember.id;

          // If backend returned image identifier/url, sync previews and value
          if ((newMember as any).image && typeof (newMember as any).image === 'string') {
            teamMember.imagePreview = (newMember as any).image;
            teamMember.image = (newMember as any).image;
          }

          // Add to local array
          this.teamMembers.push(teamMember);

          // Add to FormArray
          const memberFormGroup = new FormGroup({
            id: new FormControl(newMember.id),
            name: new FormControl(teamMember.name),
            position: new FormControl(teamMember.position),
            linkedinUrl: new FormControl(teamMember.linkedinUrl),
            description: new FormControl(teamMember.description),
            image: new FormControl(teamMember.image),
          });
          this.teamMembersFormArray.push(memberFormGroup);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.teamMembers.addMember'),
            detail: this.translateService.instant('projectEdit.teamMembers.addSuccess', {
              name: teamMember.name,
            }),
            life: 3000,
          });
        }
      }
    } catch (error) {
      // Error already handled by interceptor
      // Item will NOT be added to the UI
    }
  }

  async updateTeamMember(updatedMember: TeamMember, index: number): Promise<void> {
    // Update local array
    this.teamMembers[index] = updatedMember;

    // Update FormArray
    const memberFormGroup = this.teamMembersFormArray.at(index) as FormGroup;
    memberFormGroup.patchValue({
      name: updatedMember.name,
      position: updatedMember.position,
      linkedinUrl: updatedMember.linkedinUrl,
      description: updatedMember.description,
      image: updatedMember.image,
    });

    // Save to backend
    await this.saveTeamMember(updatedMember);
  }

  async removeTeamMember(index: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot delete team member: No project ID available');
      return;
    }

    const teamMember = this.teamMembers[index];
    if (!teamMember || !teamMember.id) {
      console.warn('Cannot delete team member: No member ID available');
      return;
    }

    try {
      // Call API to delete team member
      await this.api.invoke(adminProjectsControllerDeleteTeamMemberV1, {
        projectId: this.projectId,
        memberId: teamMember.id,
      });

      // Remove from local array
      this.teamMembers.splice(index, 1);

      // Remove from FormArray
      this.teamMembersFormArray.removeAt(index);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.teamMembers.removeMember'),
        detail: this.translateService.instant('projectEdit.teamMembers.removeSuccess', {
          name: teamMember.name,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  async saveTeamMember(teamMember: TeamMember, index?: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot save team member: No project ID available');
      return;
    }

    try {
      // Use multipart endpoint; include file only when present
      const response: any = await this.api.invoke(adminProjectsControllerAddOrUpdateTeamMemberV1, {
        projectId: this.projectId,
        body: {
          id: teamMember.id,
          name: teamMember.name,
          position: teamMember.position,
          linkedinUrl: teamMember.linkedinUrl,
          description: teamMember.description,
          file: teamMember.image instanceof File ? (teamMember.image as Blob) : undefined,
        } as any,
      });

      // Update preview/url from response for the specific member
      const updated = (response.teamMembers as any[] | undefined)?.find(
        (m: any) => m.id === teamMember.id
      );
      if (updated && updated.image && typeof updated.image === 'string') {
        teamMember.imagePreview = updated.image;
        teamMember.image = updated.image;
      }

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.teamMembers.updateMember'),
        detail: this.translateService.instant('projectEdit.teamMembers.updateSuccess', {
          name: teamMember.name,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  // Vesting categories methods
  openAddVestingCategoryDialog(): void {
    this.ref = this.dialogService.open(VestingCategoryDialogComponent, {
      header: this.translateService.instant('projectEdit.vestingCategories.addCategory'),
      width: '600px',
      modal: true,
      closable: true,
      focusOnShow: false,
    });

    this.ref.onClose.subscribe((vestingCategory: VestingCategory) => {
      if (vestingCategory) {
        this.addVestingCategory(vestingCategory);
      }
    });
  }

  async addVestingCategory(vestingCategory: VestingCategory): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot add vesting category: No project ID available');
      return;
    }

    try {
      // Prepare DTO with proper types for backend
      const dto: CreateVestingCategoryDto = {
        category: vestingCategory.category,
        walletAddress: vestingCategory.walletAddress,
        cliffPeriod: vestingCategory.cliffPeriod, // number (months)
        vestingDuration: vestingCategory.vestingDuration, // number (months)
        releasingFrequency: vestingCategory.releasingFrequency,
        tgeUnlock: vestingCategory.tgeUnlock,
        firstClaim: vestingCategory.firstClaim,
      };

      // Save to backend first
      const response = await this.api.invoke(adminProjectsControllerAddOrUpdateVestingCategoryV1, {
        projectId: this.projectId,
        body: dto,
      });

      // Only add to UI if save was successful
      if (response.vestingCategories && Array.isArray(response.vestingCategories)) {
        const newCategory = (response.vestingCategories as VestingCategory[]).find(
          (cat) => cat.category === vestingCategory.category
        );

        if (newCategory && newCategory.id) {
          // Update with real ID from backend
          vestingCategory.id = newCategory.id;

          // Add to local array
          this.vestingCategories.push(vestingCategory);

          // Add to FormArray
          const categoryFormGroup = new FormGroup({
            id: new FormControl(newCategory.id),
            category: new FormControl(vestingCategory.category),
            walletAddress: new FormControl(vestingCategory.walletAddress),
            cliffPeriod: new FormControl(vestingCategory.cliffPeriod),
            vestingDuration: new FormControl(vestingCategory.vestingDuration),
            releasingFrequency: new FormControl(vestingCategory.releasingFrequency),
            tgeUnlock: new FormControl(vestingCategory.tgeUnlock),
            firstClaim: new FormControl(vestingCategory.firstClaim),
          });
          this.vestingCategoriesFormArray.push(categoryFormGroup);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.vestingCategories.addCategory'),
            detail: this.translateService.instant('projectEdit.vestingCategories.addSuccess', {
              category: vestingCategory.category,
            }),
            life: 3000,
          });
        }
      }
    } catch (error) {
      // Error already handled by interceptor
      // Item will NOT be added to the UI
    }
  }

  openEditVestingCategoryDialog(index: number): void {
    const vestingCategory = this.vestingCategoriesFormArray.at(index).value;
    const ref = this.dialogService.open(VestingCategoryDialogComponent, {
      header: this.translateService.instant('projectEdit.vestingCategories.editTitle'),
      width: '600px',
      data: { vestingCategory: vestingCategory },
    });

    ref.onClose.subscribe((result: VestingCategory) => {
      if (result) {
        this.updateVestingCategory(index, result);
      }
    });
  }

  async updateVestingCategory(index: number, vestingCategory: VestingCategory): Promise<void> {
    // Update local array
    this.vestingCategories[index] = vestingCategory;

    // Update FormArray
    this.vestingCategoriesFormArray.at(index).patchValue(vestingCategory);

    // Save to backend
    await this.saveVestingCategory(vestingCategory);
  }

  editVestingCategory(index: number): void {
    this.openEditVestingCategoryDialog(index);
  }

  async removeVestingCategory(index: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot delete vesting category: No project ID available');
      return;
    }

    const vestingCategory = this.vestingCategories[index];
    if (!vestingCategory || !vestingCategory.id) {
      console.warn('Cannot delete vesting category: No category ID available');
      return;
    }

    try {
      // Call API to delete vesting category
      await this.api.invoke(adminProjectsControllerDeleteVestingCategoryV1, {
        projectId: this.projectId,
        categoryId: vestingCategory.id,
      });

      // Remove from local array
      this.vestingCategories.splice(index, 1);

      // Remove from FormArray
      this.vestingCategoriesFormArray.removeAt(index);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.vestingCategories.updateCategory'),
        detail: this.translateService.instant('projectEdit.vestingCategories.removeSuccess', {
          category: vestingCategory.category,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.copied'),
            detail: this.translateService.instant('common.copiedToClipboard'),
            life: 2000,
          });
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
        });
    }
  }

  async saveVestingCategory(vestingCategory: VestingCategory, index?: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot save vesting category: No project ID available');
      return;
    }

    try {
      // Prepare DTO with proper types for backend (update requires ID)
      const dto: CreateVestingCategoryDto = {
        id: vestingCategory.id,
        category: vestingCategory.category,
        walletAddress: vestingCategory.walletAddress,
        cliffPeriod: vestingCategory.cliffPeriod, // number (months)
        vestingDuration: vestingCategory.vestingDuration, // number (months)
        releasingFrequency: vestingCategory.releasingFrequency,
        tgeUnlock: vestingCategory.tgeUnlock,
        firstClaim: vestingCategory.firstClaim,
      };

      // AddOrUpdate endpoint - used for updates only (adds are handled in addVestingCategory)
      await this.api.invoke(adminProjectsControllerAddOrUpdateVestingCategoryV1, {
        projectId: this.projectId,
        body: dto,
      });

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.vestingCategories.updateCategory'),
        detail: this.translateService.instant('projectEdit.vestingCategories.updateSuccess', {
          category: vestingCategory.category,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  // Token Allocation Chart Methods
  initializeChart(): void {
    this.chartOptions = {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
    };

    // Initialize chart with current data from FormArray
    this.updateChartData();
  }

  updateChartData(): void {
    const allocations = this.tokenAllocationFormArray.value as Array<{
      name: string;
      percentage: number;
      color: string;
    }>;

    // Always create a new object to trigger change detection
    this.chartData = {
      labels: allocations.map((item) => item.name || 'Unnamed'),
      datasets: [
        {
          data: allocations.map((item) => item.percentage || 0),
          backgroundColor: allocations.map((item) => item.color || '#806EDD'),
          borderWidth: 0,
        },
      ],
    };

    // Force change detection
    this.cdr.detectChanges();
  }

  private refreshChart(): void {
    // Simply update data and trigger change detection
    this.updateChartData();
  }

  private destroyChart(): void {
    if (this.tokenChart && this.tokenChart.chart) {
      this.tokenChart.chart.destroy();
    }
    this.chartOptions = null;
    this.chartInitialized.set(false);
  }

  onChartClick(_event: MouseEvent): void {
    // Chart click handler - indicates chart is working
  }

  openAddAllocationDialog(): void {
    const defaultColors = [
      '#806EDD',
      '#5C4AB8',
      '#FFB800',
      '#00D4FF',
      '#FF6B6B',
      '#4ECDC4',
      '#95E1D3',
    ];
    const colorIndex = this.tokenAllocationFormArray.length % defaultColors.length;

    this.ref = this.dialogService.open(AllocationDialogComponent, {
      header: this.translateService.instant('projectEdit.tokenAllocation.addAllocation'),
      width: '450px',
      data: {
        colorIndex: colorIndex,
      },
    });

    this.ref.onClose.subscribe(async (allocation: TokenAllocation) => {
      if (allocation) {
        await this.addTokenAllocation(allocation);
      }
    });
  }

  async addTokenAllocation(allocation: TokenAllocation): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot add token allocation: No project ID available');
      return;
    }

    try {
      // Prepare DTO with proper types for backend
      const dto: CreateTokenAllocationDto = {
        name: allocation.name,
        percentage: allocation.percentage,
        color: allocation.color,
      };

      // Save to backend first
      const response = await this.api.invoke(adminProjectsControllerAddOrUpdateTokenAllocationV1, {
        projectId: this.projectId,
        body: dto,
      });

      // Only add to UI if save was successful
      if (response.tokenAllocation && Array.isArray(response.tokenAllocation)) {
        const newAllocation = (response.tokenAllocation as TokenAllocation[]).find(
          (alloc) => alloc.name === allocation.name
        );

        if (newAllocation && newAllocation.id) {
          // Update with real ID from backend
          allocation.id = newAllocation.id;

          // Add to FormArray
          const allocationGroup = new FormGroup({
            id: new FormControl(newAllocation.id),
            color: new FormControl(allocation.color),
            name: new FormControl(allocation.name),
            percentage: new FormControl(allocation.percentage),
          });
          this.tokenAllocationFormArray.push(allocationGroup);

          // Update chart
          this.updateChartData();
          this.cdr.detectChanges();

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.tokenAllocation.addAllocation'),
            detail: this.translateService.instant('projectEdit.tokenAllocation.addSuccess', {
              name: allocation.name,
            }),
            life: 3000,
          });
        }
      }
    } catch (error) {
      // Error already handled by interceptor
      // Item will NOT be added to the UI
    }
  }

  openEditAllocationDialog(index: number): void {
    const allocation = this.tokenAllocationFormArray.at(index).value;

    this.ref = this.dialogService.open(AllocationDialogComponent, {
      header: this.translateService.instant('projectEdit.tokenAllocation.editAllocation'),
      width: '450px',
      data: {
        allocation: allocation,
      },
    });

    this.ref.onClose.subscribe(async (updatedAllocation: TokenAllocation) => {
      if (updatedAllocation) {
        // Get the current allocation to preserve the ID
        const currentAllocation = this.tokenAllocationFormArray.at(index).value;
        updatedAllocation.id = currentAllocation.id;

        // Update the form control values individually
        const control = this.tokenAllocationFormArray.at(index) as FormGroup;
        control.get('color')?.setValue(updatedAllocation.color);
        control.get('name')?.setValue(updatedAllocation.name);
        control.get('percentage')?.setValue(updatedAllocation.percentage);

        // Manually trigger chart update and change detection
        this.updateChartData();
        this.cdr.detectChanges();

        // Save to backend
        await this.saveTokenAllocation(updatedAllocation);
      }
    });
  }

  addAllocationItem(): void {
    this.openAddAllocationDialog();
  }

  async removeAllocationItem(index: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot delete token allocation: No project ID available');
      this.tokenAllocationFormArray.removeAt(index);
      return;
    }

    const allocation = this.tokenAllocationFormArray.at(index).value;
    if (!allocation || !allocation.id) {
      console.warn('Cannot delete token allocation: No allocation ID available');
      this.tokenAllocationFormArray.removeAt(index);
      return;
    }

    try {
      // Call API to delete token allocation
      await this.api.invoke(adminProjectsControllerDeleteTokenAllocationV1, {
        projectId: this.projectId,
        allocationId: allocation.id,
      });

      // Remove from FormArray
      this.tokenAllocationFormArray.removeAt(index);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.tokenAllocation.removeAllocation'),
        detail: this.translateService.instant('projectEdit.tokenAllocation.removeSuccess', {
          name: allocation.name,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  async saveTokenAllocation(allocation: TokenAllocation, index?: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot save token allocation: No project ID available');
      return;
    }

    try {
      // Prepare DTO with proper types for backend (update requires ID)
      const dto: CreateTokenAllocationDto = {
        id: allocation.id,
        name: allocation.name,
        percentage: allocation.percentage,
        color: allocation.color,
      };

      // AddOrUpdate endpoint - used for updates only (adds are handled in addTokenAllocation)
      await this.api.invoke(adminProjectsControllerAddOrUpdateTokenAllocationV1, {
        projectId: this.projectId,
        body: dto,
      });

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.tokenAllocation.updateAllocation'),
        detail: this.translateService.instant('projectEdit.tokenAllocation.updateSuccess', {
          name: allocation.name,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  getTotalAllocationPercentage(): number {
    return this.tokenAllocationFormArray.value.reduce(
      (total: number, item: { percentage?: number }) => {
        return total + (item.percentage || 0);
      },
      0
    );
  }

  /**
   * Force complete chart reinitialization with current FormArray data
   */
  private forceChartReinitialization(): void {
    // Initialize chart options
    this.chartOptions = {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
    };

    // Get current allocations from FormArray
    const currentAllocations = this.tokenAllocationFormArray.value as Array<{
      name: string;
      percentage: number;
      color: string;
    }>;

    // Create chart data from current FormArray state
    this.chartData = {
      labels: currentAllocations.map((item) => item.name || 'Unnamed'),
      datasets: [
        {
          data: currentAllocations.map((item) => item.percentage || 0),
          backgroundColor: currentAllocations.map((item) => item.color || '#806EDD'),
          borderWidth: 0,
        },
      ],
    };

    // Force change detection to ensure chart rerenders
    this.cdr.detectChanges();
    this.chartInitialized.set(true);
  }

  // Updates methods
  openAddUpdateDialog(): void {
    this.ref = this.dialogService.open(UpdateDialogComponent, {
      header: this.translateService.instant('projectEdit.updates.addUpdate'),
      width: '600px',
      modal: true,
      closable: true,
      data: { projectId: this.projectId },
    });

    this.ref.onClose.subscribe((update: Update) => {
      if (update) {
        this.addUpdate(update);
      }
    });
  }

  openEditUpdateDialog(update: Update, index: number): void {
    this.ref = this.dialogService.open(UpdateDialogComponent, {
      header: this.translateService.instant('projectEdit.updates.editUpdate'),
      width: '600px',
      modal: true,
      closable: true,
      data: { update: update, projectId: this.projectId },
    });

    this.ref.onClose.subscribe((updatedUpdate: Update) => {
      if (updatedUpdate) {
        this.updateUpdate(updatedUpdate, index);
      }
    });
  }

  onEditUpdate(event: { update: Update; index: number }): void {
    this.openEditUpdateDialog(event.update, event.index);
  }

  async addUpdate(update: Update): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot add update: No project ID available');
      return;
    }

    try {
      // Use generated API (multipart/form-data) like team-members; include file only when present
      const response = await this.api.invoke(adminProjectsControllerAddOrUpdateUpdateV1, {
        projectId: this.projectId,
        body: {
          title: update.title,
          text: update.text,
          publishDate:
            update.publishDate instanceof Date
              ? update.publishDate.toISOString()
              : (update.publishDate as any),
          file: update.coverImage instanceof File ? (update.coverImage as Blob) : undefined,
        } as any,
      });

      // Only add to UI if save was successful
      if (response.updates && Array.isArray(response.updates)) {
        const newUpdate = (response.updates as Update[]).find(
          (upd: any) => upd.title === update.title
        );

        if (newUpdate && newUpdate.id) {
          // Update with real ID from backend
          update.id = newUpdate.id;

          // If backend returned a coverImage URL, set preview accordingly
          if ((newUpdate as any).coverImage && typeof (newUpdate as any).coverImage === 'string') {
            update.coverImagePreview = (newUpdate as any).coverImage;
            // Clear File object once uploaded
            if (update.coverImage instanceof File) {
              update.coverImage = (newUpdate as any).coverImage;
            }
          }

          // Add to local array
          this.updates.push(update);

          // Add to FormArray
          const updateFormGroup = new FormGroup({
            id: new FormControl(newUpdate.id),
            title: new FormControl(update.title),
            text: new FormControl(update.text),
            publishDate: new FormControl(update.publishDate),
            coverImage: new FormControl(update.coverImage),
          });
          this.updatesFormArray.push(updateFormGroup);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('projectEdit.updates.addUpdate'),
            detail: this.translateService.instant('projectEdit.updates.addSuccess', {
              title: update.title,
            }),
            life: 3000,
          });
        }
      }
    } catch (error) {
      // Error already handled by interceptor
      // Item will NOT be added to the UI
    }
  }

  async updateUpdate(updatedUpdate: Update, index: number): Promise<void> {
    // Update local array
    this.updates[index] = updatedUpdate;

    // Update FormArray
    const updateFormGroup = this.updatesFormArray.at(index) as FormGroup;
    updateFormGroup.patchValue({
      title: updatedUpdate.title,
      text: updatedUpdate.text,
      publishDate: updatedUpdate.publishDate,
      coverImage: updatedUpdate.coverImage,
    });

    // Save to backend
    await this.saveUpdate(updatedUpdate);
  }

  async removeUpdate(index: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot delete update: No project ID available');
      return;
    }

    const update = this.updates[index];
    if (!update || !update.id) {
      console.warn('Cannot delete update: No update ID available');
      return;
    }

    try {
      // Call API to delete update
      await this.api.invoke(adminProjectsControllerDeleteUpdateV1, {
        projectId: this.projectId,
        updateId: update.id,
      });

      // Remove from local array
      this.updates.splice(index, 1);

      // Remove from FormArray
      this.updatesFormArray.removeAt(index);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.updates.removeUpdate'),
        detail: this.translateService.instant('projectEdit.updates.removeSuccess', {
          title: update.title,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  async saveUpdate(update: Update, index?: number): Promise<void> {
    if (!this.projectId) {
      console.warn('Cannot save update: No project ID available');
      return;
    }

    try {
      const response: any = await this.api.invoke(adminProjectsControllerAddOrUpdateUpdateV1, {
        projectId: this.projectId!,
        body: {
          id: update.id,
          title: update.title,
          text: update.text,
          publishDate:
            update.publishDate instanceof Date
              ? update.publishDate.toISOString()
              : (update.publishDate as any),
          file: update.coverImage instanceof File ? (update.coverImage as Blob) : undefined,
        } as any,
      });

      // Update preview/url from response
      const updated = (response.updates as any[]).find((u: any) => u.id === update.id) || null;
      if (updated && updated.coverImage && typeof updated.coverImage === 'string') {
        update.coverImagePreview = updated.coverImage;
        update.coverImage = updated.coverImage;
      }

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('projectEdit.updates.updateUpdate'),
        detail: this.translateService.instant('projectEdit.updates.updateSuccess', {
          title: update.title,
        }),
        life: 3000,
      });
    } catch (error) {
      // Error already handled by interceptor
    }
  }

  // Image upload methods
  triggerImageUpload(): void {
    const fileInput = document.querySelector(
      'input[type="file"][accept*="image"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onImageFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    // Clear previous errors
    this.projectImageError = null;

    if (!file) {
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      this.projectImageError = 'projectEdit.invalidImageType';
      console.error('Invalid file type:', file.type);
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      this.projectImageError = 'projectEdit.imageTooLarge';
      console.error('File too large:', file.size);
      return;
    }

    // Process the image
    this.processImageFile(file);

    // Reset input for potential re-upload
    input.value = '';
  }

  private processImageFile(file: File): void {
    const reader = new FileReader();

    reader.onload = async (event) => {
      const result = event.target?.result as string;
      if (result) {
        this.projectImagePreview.set(result);
        // Don't trigger autosave for image changes (use emitEvent: false)
        this.projectImageControl.setValue(file, { emitEvent: false });

        // Upload image immediately (user action!)
        await this.uploadProjectImageImmediately(file);
      } else {
        console.error('Failed to load image');
        this.projectImageError = 'projectEdit.imageUploadError';
      }
    };

    reader.onerror = () => {
      console.error('FileReader error');
      this.projectImageError = 'projectEdit.imageUploadError';
    };

    reader.readAsDataURL(file);
  }

  async onProjectImageSelect(event: { files: File[] }): Promise<void> {
    const file = event.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5000000) {
        this.projectImageError = 'projectEdit.imageTooLarge';
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.projectImageError = 'projectEdit.invalidImageType';
        return;
      }

      // Clear any previous errors
      this.projectImageError = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.projectImagePreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Update form control with File object (don't trigger autosave)
      this.projectImageControl.setValue(file, { emitEvent: false });

      // Upload image immediately (user action!)
      // This works for both new and existing projects
      await this.uploadProjectImageImmediately(file);
    }
  }

  /**
   * Upload project image immediately when user selects a file
   * This is a user action, so browser allows file upload
   * For new projects, this will create the project first
   * Properly manages save state indicators like autosave does
   */
  private async uploadProjectImageImmediately(file: File): Promise<void> {
    // Set indicator to saving (same as autosave)
    this.headerService.setSaveIndicator({ status: 'saving' });
    this.autoSaving.set(true);

    try {
      let response: any;

      if (this.projectId && this.projectId !== 'new') {
        // Existing project - just update with image
        response = await this.api.invoke(adminProjectsControllerUpdateProjectV1, {
          id: this.projectId,
          body: {
            projectImage: file,
          } as any,
        });
      } else {
        // New project - create with image
        response = await this.api.invoke(adminProjectsControllerCreateProjectV1, {
          body: {
            projectImage: file,
          } as any,
        });

        // Update component state with the new project ID
        if (response && response.id) {
          this.projectId = response.id;
          this.isEditMode = true;
          this.projectStatus.set(response.status);

          // Update the URL to reflect the new project ID (without page reload)
          this.router.navigate(['/projects', response.id], { replaceUrl: true });

          // Update header to show "Edit Project" instead of "Add Project"
          this.headerService.setTitle(this.translateService.instant('projectEdit.editTitle'));
          this.headerService.setSubtitle(this.translateService.instant('projectEdit.editSubtitle'));

          // Update header buttons to reflect edit mode
          this.updateHeaderButtons();
        }
      }

      // Update form control with the returned image URL
      if (response.projectImage) {
        // Clear File object and set URL without triggering autosave
        this.projectImageControl.setValue(null, { emitEvent: false });
        this.projectImagePreview.set(response.projectImage); // Set URL for display

        // Set indicator to saved with timestamp (same as autosave)
        if (response.updatedAt) {
          this.headerService.setSaveIndicator({
            status: 'saved',
            lastSavedAt: new Date(response.updatedAt),
          });
        }

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('projectEdit.success.imageUploadedTitle'),
          detail: this.translateService.instant('projectEdit.success.imageUploadedMessage'),
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Image upload error:', error);
      this.projectImageError = 'projectEdit.imageUploadError';

      // Reset to unsaved state on error
      this.headerService.setSaveIndicator({ status: 'unsaved' });

      this.messageService.add({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error instanceof Error ? error.message : 'Unknown error occurred',
        life: 5000,
      });
    } finally {
      // Clear saving state
      this.autoSaving.set(false);
    }
  }

  /**
   * Load initial project owners for autocomplete dropdown
   */
  private async loadInitialProjectOwners(): Promise<void> {
    try {
      const response = await this.api.invoke(adminProjectOwnersControllerGetAllProjectOwners, {
        limit: 10,
        page: 1,
        sortBy: 'createdAt' as any,
        sortOrder: 'desc' as any,
      });

      console.log('Initial project owners loaded:', response);
      this.projectOwnerSuggestions.set(response.data || []);
    } catch (error) {
      console.error('Failed to load initial project owners:', error);
      this.projectOwnerSuggestions.set([]);
    }
  }

  /**
   * Search project owners by email for autocomplete
   */
  async searchProjectOwners(event: { query: string }): Promise<void> {
    const searchTerm = event.query.trim();

    console.log('Search project owners called with:', searchTerm);

    // If empty query, show initial suggestions
    if (!searchTerm || searchTerm.length < 2) {
      await this.loadInitialProjectOwners();
      return;
    }

    this.projectOwnerSearchLoading.set(true);

    try {
      const response = await this.api.invoke(adminProjectOwnersControllerGetAllProjectOwners, {
        search: searchTerm,
        limit: 10,
        page: 1,
      });

      console.log('Project owner search response:', response);
      this.projectOwnerSuggestions.set(response.data || []);
    } catch (error) {
      console.error('Project owner search error:', error);
      this.projectOwnerSuggestions.set([]);
    } finally {
      this.projectOwnerSearchLoading.set(false);
    }
  }

  /**
   * Handle project owner selection from autocomplete
   */
  onProjectOwnerSelect(event: any): void {
    const owner = event.value as ProjectOwnerResponseAdminAdminDto;
    this.selectedProjectOwner.set(owner);
    this.projectOwnerControl.setValue(owner.id);
    this.projectOwnerEmailControl.setValue(owner.email); // Set email for display
  }

  /**
   * Handle URL field input - sets editing state to hide errors while typing
   * Triggers debounced validation after user stops typing
   */
  onUrlFieldInput(fieldName: string): void {
    // Immediately hide error when user starts typing
    this.setUrlFieldEditingState(fieldName, true);

    // Emit to debounce stream - will show error after 800ms of no typing
    this.urlFieldTyping$.next(fieldName);
  }

  /**
   * Set the editing state for a specific URL field
   */
  private setUrlFieldEditingState(fieldName: string, isEditing: boolean): void {
    switch (fieldName) {
      case 'ownerUrl':
        this.isEditingOwnerUrl.set(isEditing);
        break;
      case 'twitterUrl':
        this.isEditingTwitterUrl.set(isEditing);
        break;
      case 'linkedinUrl':
        this.isEditingLinkedinUrl.set(isEditing);
        break;
      case 'telegramUrl':
        this.isEditingTelegramUrl.set(isEditing);
        break;
      case 'discordUrl':
        this.isEditingDiscordUrl.set(isEditing);
        break;
      case 'whitepaperUrl':
        this.isEditingWhitepaperUrl.set(isEditing);
        break;
      case 'tokenomicsUrl':
        this.isEditingTokenomicsUrl.set(isEditing);
        break;
    }
  }

  /**
   * Handle URL field blur - ensures error is shown if field is invalid
   */
  onUrlFieldBlur(fieldName: string): void {
    // When field loses focus, immediately show error if invalid
    this.setUrlFieldEditingState(fieldName, false);
  }

  /**
   * Handle project owner clear
   */
  onProjectOwnerClear(): void {
    this.selectedProjectOwner.set(null);
    this.projectOwnerControl.setValue(null);
    this.projectOwnerEmailControl.setValue('');
  }

  /**
   * Handle name input - show temporary error when trying to exceed max length
   */
  onNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Hide error when below limit
    if (input.value.length < 30) {
      this.showNameMaxLengthError.set(false);
      if (this.nameMaxLengthTimer) {
        clearTimeout(this.nameMaxLengthTimer);
      }
    }
  }

  /**
   * Handle name keydown - detect when user tries to type at max length
   */
  onNameKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    // Check if at max length and user is trying to add more characters
    // Allow special keys: Backspace, Delete, Arrow keys, Tab, etc.
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    const isModifierKey = event.ctrlKey || event.metaKey || event.altKey;

    // Only show error when trying to ADD characters beyond the limit
    if (input.value.length >= 30 && !allowedKeys.includes(event.key) && !isModifierKey) {
      // Show error message
      this.showNameMaxLengthError.set(true);

      // Clear existing timer
      if (this.nameMaxLengthTimer) {
        clearTimeout(this.nameMaxLengthTimer);
      }

      // Hide error after 3 seconds
      this.nameMaxLengthTimer = setTimeout(() => {
        this.showNameMaxLengthError.set(false);
      }, 3000);
    }
  }

  /**
   * Handle name paste - show error when pasted content would exceed limit
   */
  onNamePaste(event: ClipboardEvent): void {
    const input = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text') || '';
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    // Calculate what the value would be after paste
    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);
    const newValue = beforeSelection + pastedText + afterSelection;

    // If pasted content would exceed limit, show error
    if (newValue.length > 30) {
      this.showNameMaxLengthError.set(true);

      // Clear existing timer
      if (this.nameMaxLengthTimer) {
        clearTimeout(this.nameMaxLengthTimer);
      }

      // Hide error after 3 seconds
      this.nameMaxLengthTimer = setTimeout(() => {
        this.showNameMaxLengthError.set(false);
      }, 3000);
    }
  }

  /**
   * Handle description input - show temporary error when trying to exceed max length
   */
  onDescriptionInput(event: Event): void {
    const input = event.target as HTMLTextAreaElement;
    // Hide error when below limit
    if (input.value.length < 600) {
      this.showDescriptionMaxLengthError.set(false);
      if (this.descriptionMaxLengthTimer) {
        clearTimeout(this.descriptionMaxLengthTimer);
      }
    }
  }

  /**
   * Handle description keydown - detect when user tries to type at max length
   */
  onDescriptionKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLTextAreaElement;
    // Check if at max length and user is trying to add more characters
    // Allow special keys: Backspace, Delete, Arrow keys, Tab, etc.
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
      'Enter',
    ];
    const isModifierKey = event.ctrlKey || event.metaKey || event.altKey;

    // Only show error when trying to ADD characters beyond the limit
    if (input.value.length >= 600 && !allowedKeys.includes(event.key) && !isModifierKey) {
      // Show error message
      this.showDescriptionMaxLengthError.set(true);

      // Clear existing timer
      if (this.descriptionMaxLengthTimer) {
        clearTimeout(this.descriptionMaxLengthTimer);
      }

      // Hide error after 3 seconds
      this.descriptionMaxLengthTimer = setTimeout(() => {
        this.showDescriptionMaxLengthError.set(false);
      }, 3000);
    }
  }

  /**
   * Handle description paste - show error when pasted content would exceed limit
   */
  onDescriptionPaste(event: ClipboardEvent): void {
    const input = event.target as HTMLTextAreaElement;
    const pastedText = event.clipboardData?.getData('text') || '';
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    // Calculate what the value would be after paste
    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);
    const newValue = beforeSelection + pastedText + afterSelection;

    // If pasted content would exceed limit, show error
    if (newValue.length > 600) {
      this.showDescriptionMaxLengthError.set(true);

      // Clear existing timer
      if (this.descriptionMaxLengthTimer) {
        clearTimeout(this.descriptionMaxLengthTimer);
      }

      // Hide error after 3 seconds
      this.descriptionMaxLengthTimer = setTimeout(() => {
        this.showDescriptionMaxLengthError.set(false);
      }, 3000);
    }
  }

  onProjectImageUpload(_event: unknown): void {
    // Handle successful upload if needed - event structure not documented in PrimeNG
  }

  /**
   * Check if the current viewport is mobile size
   */
  isMobile(): boolean {
    return window.innerWidth < 768;
  }
}
